import { useReactiveVar } from '@apollo/client'
import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useProjectQuery } from '../../generated/graphql'
import {
  dictionariesVar,
  projectVar,
  selectedVersionsVar,
} from '../../apollo/reactive-vars'
import {
  createDictionaries,
  emptyDictionaries,
} from '../common/util/dictionary-util'

export type ProjectSubLayoutParams = {
  id: string
}

export type ProjectSubLayoutProps = {
  children: React.ReactElement
}

export function ProjectSubDataLayout({
  children,
}: ProjectSubLayoutProps): React.ReactElement {
  const { id } = useParams<ProjectSubLayoutParams>()
  const { data } = useProjectQuery({ variables: { id } })
  const project = useReactiveVar(projectVar)
  const { t } = useTranslation()

  useEffect(
    () =>
      // Clean up on component unmount
      () => {
        projectVar(null)
        selectedVersionsVar([])
        dictionariesVar(emptyDictionaries)
      },
    [],
  )

  useEffect(() => {
    if (!data || !data.project) {
      projectVar(null)
      return
    }
    projectVar(data.project)
  }, [data])

  useEffect(() => {
    const selectedVersions = selectedVersionsVar()
    const updatedVersions = selectedVersions.map(
      ({ energyCalculator, ...version }) => ({
        ...(project?.versions.find((v) => v.id === version.id) ?? version),
        energyCalculator,
      }),
    )
    selectedVersionsVar(updatedVersions)
  }, [project?.versions])

  useEffect(() => {
    if (project?.versions) {
      const dictionaries = createDictionaries(project.versions, t)
      dictionariesVar(dictionaries)
    }
  }, [project?.versions, t])

  return <>{children}</>
}
